import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useViewer } from '@/entities/viewer';
import { Icons } from '@/shared/ui';

import styles from './Sections.module.css';
import type { SectionId } from './sections';

const POINTS = ['p1', 'p2', 'p3', 'p4'] as const;

/** Three short sentences that tie the areas together: plan, keep records, work together. */
export function Overview() {
  const { t } = useTranslation();
  const items = ['plan', 'store', 'together'] as const;
  return (
    <section className={styles.section} aria-labelledby="landing-overview-title">
      <h2 id="landing-overview-title" className={styles.overviewTitle}>
        {t('landing.overview.title')}
      </h2>
      <ul className={styles.overview}>
        {items.map((item, index) => (
          <li key={item}>
            <span className={styles.stepNo} aria-hidden>
              {index + 1}
            </span>
            <strong>{t(`landing.overview.${item}.title`)}</strong>
            <span className={styles.stepText}>{t(`landing.overview.${item}.text`)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One capability: a short text with four points beside a picture of the real application. `flip`
 * puts the picture on the left; on a narrow screen the text always comes first.
 */
export function FeatureSection({
  id,
  flip = false,
  children,
}: {
  id: SectionId;
  flip?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const heading = `landing-${id}-title`;
  return (
    <section id={id} className={styles.section} aria-labelledby={heading}>
      <div className={`${styles.feature} ${flip ? styles.flip : ''}`}>
        <div className={styles.copy}>
          <p className={styles.eyebrow}>{t(`landing.sections.${id}.eyebrow`)}</p>
          <h2 id={heading} className={styles.title}>
            {t(`landing.sections.${id}.title`)}
          </h2>
          <p className={styles.text}>{t(`landing.sections.${id}.text`)}</p>
          <ul className={styles.points}>
            {POINTS.map((point) => (
              <li key={point}>
                <Icons.check />
                <span>{t(`landing.sections.${id}.points.${point}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.visual}>{children}</div>
      </div>
    </section>
  );
}

/** Three plain steps, kept short: the page above has already explained the product. */
export function HowItWorks() {
  const { t } = useTranslation();
  const steps = ['s1', 's2', 's3'] as const;
  return (
    <section className={styles.section} aria-labelledby="landing-how-title">
      <h2 id="landing-how-title" className={styles.overviewTitle}>
        {t('landing.how.title')}
      </h2>
      <ol className={styles.overview}>
        {steps.map((step, index) => (
          <li key={step}>
            <span className={styles.stepNo}>{index + 1}</span>
            <strong>{t(`landing.how.${step}`)}</strong>
            <span className={styles.stepText}>{t(`landing.how.${step}t`)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Actions({ className }: { className?: string }): ReactNode {
  const { t } = useTranslation();
  const viewer = useViewer();
  const signedIn = viewer.status === 'authenticated';
  return (
    <div className={className}>
      {signedIn ? (
        <Link to="/app" className={styles.primary}>
          {t('landing.cta.open')}
        </Link>
      ) : (
        <>
          <Link to="/register" className={styles.primary}>
            {t('landing.cta.start')}
          </Link>
          <Link to="/login" className={styles.secondary}>
            {t('landing.cta.signIn')}
          </Link>
        </>
      )}
    </div>
  );
}

export function FinalCta() {
  const { t } = useTranslation();
  return (
    <section className={`${styles.section} ${styles.final}`} aria-labelledby="landing-final-title">
      <h2 id="landing-final-title" className={styles.finalTitle}>
        {t('landing.final.title')}
      </h2>
      <p className={styles.finalText}>{t('landing.final.text')}</p>
      <Actions className={styles.finalActions} />
    </section>
  );
}

/** A product picture in a quiet window frame (the picture itself describes itself with alt text). */
export function Frame({ children, phone = false }: { children: ReactNode; phone?: boolean }) {
  return <figure className={phone ? styles.phoneFrame : styles.frame}>{children}</figure>;
}
